<template>
  <div class="h-screen sm:bg-corner-gradient overflow-y-auto flex flex-col pt-safe pb-safe">
    <div class="absolute top-3 left-4">
      <img v-if="!isAttendanceMode" class="hidden sm:block h-8" src="/icon.png" />
      <l-icon
        v-if="isAttendanceMode"
        class="text-n-0 cursor-pointer"
        name="arrow-left"
        size="large"
        @click="isAttendanceMode = false"
      />
    </div>
    <div
      v-if="shiftsEnabled && selectedCashTill && tills.cash.length > 1"
      class="px-12 pt-3 flex justify-center"
    >
      <div class="w-full sm:w-auto">
        <l-select
          v-model="currentlySelectedCashTill"
          :options="tillOptions"
          icon="register"
          size="small"
          icon-position="left"
        />
      </div>
    </div>
    <div v-if="isElectron" class="absolute top-0 right-0 m-8 flex">
      <l-button icon="minus" type="text" size="large" @click="() => minimizeApp()" />
      <l-button icon="power" type="text" size="large" @click="() => closeApp()" />
    </div>
    <waiter-selector />
    <div
      v-if="!isAttendanceMode"
      class="mt-auto pb-8 pt-8 mr-14 ml-14 flex flex-col items-center sm:flex-row gap-2 sm:justify-between"
    >
      <l-button
        v-if="config.lastProductAttendanceEnabled && !isAttendanceMode"
        type="secondary"
        size="small"
        color="white"
        icon="clockwise"
        class="max-w-72 sm:max-w-48 w-full bg-n-800"
        @click="isAttendanceMode = true"
      >
        {{ t('employees.attendance.title') }}
      </l-button>

      <l-button
        v-if="shiftsEnabled && !isAttendanceMode"
        type="secondary"
        size="small"
        color="white"
        icon="table"
        class="max-w-72 sm:max-w-48 w-full bg-n-800"
        :loading="loadingLastZReport"
        @click="printZReport()"
      >
        {{ t('cash-amount.print-last-z-report') }}
      </l-button>
    </div>
    <clock-in-modal
      v-if="clockingEmployee"
      :employee="clockingEmployee"
      :type="hasClockedIn(clockingEmployee) ? 'clock-out' : 'clock-in'"
      @done="safeSelectEmployee"
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LIcon, LSelect } from '@last/core-ui/paprika'

import ticketPrinter from '@/integrations/printer/ticketPrinter'
import { useAuthStore } from '@/store/auth'
import { useBeepersStore } from '@/store/beepers'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import { useCouriersStore } from '@/store/couriers'
import { useEmployeeStore } from '@/store/employee'
import { useFloorplanStore } from '@/store/floorplan'
import { usePromotionsStore } from '@/store/promotions'
import { useStatusStore } from '@/store/status'
import { useTillStore } from '@/store/till'
import { isElectron } from '@/utils/device-type'
import { closeApp, minimizeApp } from '@/utils/electron'

import ClockInModal from './ClockInModal.vue'
import WaiterSelector from './WaiterSelector.vue'

const floorplanStore = useFloorplanStore()
const authStore = useAuthStore()
const tillStore = useTillStore()
const configStore = useConfigStore()
const catalogStore = useCatalogStore()
const promotionsStore = usePromotionsStore()
const couriersStore = useCouriersStore()
const statusStore = useStatusStore()
const beepersStore = useBeepersStore()
const employeeStore = useEmployeeStore()

const { t } = useI18n()

const { selectedCashTill, shiftsEnabled } = storeToRefs(tillStore)
const { tills, config } = storeToRefs(configStore)
const { clockingEmployee, isAttendanceMode, currentlySelectedCashTill } = storeToRefs(employeeStore)
const { hasClockedIn, safeSelectEmployee } = employeeStore

const loadingLastZReport = ref(false)

const tillOptions = computed(() => {
  return tills.value.cash.map(till => ({
    label: till.name,
    value: till.id
  }))
})

onMounted(async () => {
  await Promise.allSettled([
    authStore.refreshEmployees(),
    statusStore.updateCurrentStatus(),
    catalogStore.refreshCatalogs(),
    statusStore.refreshErrorMessages(),
    floorplanStore.refreshFloorplans(),
    authStore.refreshBillingStatus(),
    configStore.refreshConfig(),
    beepersStore.refreshBeepers(),
    promotionsStore.refreshPromotions(),
    couriersStore.refreshCouriers()
  ])
  selectFirstAvailableTillIfNeeded()
})

function selectFirstAvailableTillIfNeeded(): void {
  if (
    tills.value.cash &&
    tills.value.cash[0]?.id &&
    (!selectedCashTill.value?.id ||
      !tills?.value.cash.map(t => t.id).includes(selectedCashTill.value?.id) ||
      !currentlySelectedCashTill.value)
  ) {
    currentlySelectedCashTill.value = tills.value.cash[0].id
  }
}

async function printZReport() {
  if (loadingLastZReport.value) return
  loadingLastZReport.value = true
  await ticketPrinter.printZReport()
  loadingLastZReport.value = false
}
</script>
